import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Object clone performance: preliminary results",
  "description": "",
  "published": "2016-09-15T00:00:00.000Z",
  "redirect_from": ["/blog/object-clone-performance-preliminary-results/swizec/7030"],
  "categories": "coding, JavaScript, Technical",
  "hero": "./img/N1i7cv6.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Spurred by debate on `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/a-puzzle-in-javascript-objects/swizec/7014"
      }}>{`A Puzzle in JavaScript objects`}</a>{`, I wanted to find the best way to clone objects in JavaScript. In this scenario, “best” means “fastest on average”.`}</p>
    <p>{`I’m using the dataset from `}<a parentName="p" {...{
        "href": "http://swizec.github.io/h1b-software-salaries/#2014-ca-engineer"
      }}>{`this visualization on H1B salaries`}</a>{` as a quick way to build many objects. The dataset contains 81,123 objects with 9 keys each.`}</p>
    <p>{`The experiment is set up like this:`}</p>
    <ul>
      <li parentName="ul">{`load & parse data`}</li>
      <li parentName="ul">{`run clone experiments in series`}</li>
      <li parentName="ul">{`each experiment makes 10 deep copies of the dataset`}</li>
      <li parentName="ul">{`“deep” is allowed to mean just the 2 levels required (objects in array don’t contain more objects)`}</li>
      <li parentName="ul">{`present average runtime of each clone`}</li>
    </ul>
    <p>{`Each individual experiment uses this runner:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`runner(name, method) {
        const times = d3.range(0, this.N).map(() => {
            const t1 = new Date();

            let copy = method(this.state.data);

            const t2 = new Date();
            return t2 - t1;
        });

        let results = this.state.results;
        results.push({name: name,
                      avg: d3.mean(times)});

        this.setState({results: results});
    }
`}</code></pre>
    <p>{`If you spot a problem with that setup, tell me.`}</p>
    <p>{`Preliminary results, running on my laptop in Chrome 53, are a bit strange. Iterating through the array and doing `}<inlineCode parentName="p">{`Object.assign`}</inlineCode>{` is fastest, and `}<inlineCode parentName="p">{`_.cloneDeep`}</inlineCode>{` is slowest.`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "890px",
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
      `}<a parentName="span" {...{
          "className": "gatsby-resp-image-link",
          "href": "/static/5af4438a4332809b92c64fa59df61665/7a3d6/N1i7cv6.png",
          "style": {
            "display": "block"
          },
          "target": "_blank",
          "rel": "noopener"
        }}>{`
    `}<span parentName="a" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "22.869955156950674%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'91\\'%20viewBox=\\'0%200%20400%2091\\'%20preserveAspectRatio=\\'none\\'/%3e')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/5af4438a4332809b92c64fa59df61665/ca0a1/N1i7cv6.webp 223w", "/static/5af4438a4332809b92c64fa59df61665/75680/N1i7cv6.webp 445w", "/static/5af4438a4332809b92c64fa59df61665/8d1ba/N1i7cv6.webp 890w", "/static/5af4438a4332809b92c64fa59df61665/a66df/N1i7cv6.webp 990w"],
              "sizes": "(max-width: 890px) 100vw, 890px",
              "type": "image/webp"
            }}></source>{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/5af4438a4332809b92c64fa59df61665/e92b6/N1i7cv6.png 223w", "/static/5af4438a4332809b92c64fa59df61665/e66bf/N1i7cv6.png 445w", "/static/5af4438a4332809b92c64fa59df61665/4ef49/N1i7cv6.png 890w", "/static/5af4438a4332809b92c64fa59df61665/7a3d6/N1i7cv6.png 990w"],
              "sizes": "(max-width: 890px) 100vw, 890px",
              "type": "image/png"
            }}></source>{`
          `}<img parentName="picture" {...{
              "className": "gatsby-resp-image-image",
              "src": "/static/5af4438a4332809b92c64fa59df61665/4ef49/N1i7cv6.png",
              "alt": "N1i7cv6",
              "title": "N1i7cv6",
              "loading": "lazy",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              }
            }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span></p>
    <p>{`I want to add a more context-aware Lodash approach, Immutable.js, and a few different browsers. Then make some graphs :)`}</p>
    <p>{`What’s your favorite way to clone objects in JavaScript? I’ll add it to the test.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      